import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import { decompile } from '@freelog/nmr_translator';
import { ruleMatchAndResult } from '@/models/informalNodeManagerPage';
import type { IRules, RuleMatchAndResultReturn } from '@/models/informalNodeManagerPage';
import fMessage from '@/components/fMessage';
import { history } from '@@/core/history';
import { fileAttrUnits } from '@/utils/format';
import { mergeRules } from './helpers';
import type {
  ChangeAction,
  FetchInformalExhibitInfoAction,
  OnChange_Exhibit_OnlineSwitch_Action,
  OnChange_Theme_OnlineSwitch_Action,
  OnChanged_ExhibitAuthorized_Action,
  InformExhibitInfoPageModelState,
} from './types';

export const exhibitEffects = {
  * fetchInformalExhibitInfo({ payload }: FetchInformalExhibitInfoAction, { call, select, put }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const informExhibitID: string = payload?.informExhibitID !== undefined ? payload.informExhibitID : informExhibitInfoPage.exhibit_ID;

    const params: Parameters<typeof FServiceAPI.InformalNode.testResourceDetails>[0] = {
      testResourceId: informExhibitID,
    };
    const { data: testResourceDetail } = yield call(FServiceAPI.InformalNode.testResourceDetails, params);
    if (!testResourceDetail || testResourceDetail.userId !== FUtil.Tool.getUserIDByCookies()) {
      history.replace(FUtil.LinkTo.exception403({}));
      return;
    }

    const params4: Parameters<typeof FServiceAPI.Node.details>[0] = {
      nodeId: testResourceDetail.nodeId,
    };

    const { data: data4 } = yield call(FServiceAPI.Node.details, params4);

    if (data4?.status === -1) {
      history.replace(FUtil.LinkTo.nodeDeleted({ nodeID: testResourceDetail.nodeId }));
      return;
    }

    let relation: InformExhibitInfoPageModelState['side_Resource_Relation'] = null;

    const actualOriginInfo = testResourceDetail.stateInfo.replaceInfo.rootResourceReplacer || testResourceDetail.originInfo;

    if (actualOriginInfo.type === 'resource') {
      const params2: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: actualOriginInfo.id,
      };

      const { data: data2 } = yield call(FServiceAPI.Resource.info, params2);

      relation = {
        cardTitle: '关联资源',
        identity: 'resource',
        id: data2.resourceId,
        name: data2.resourceName,
        type: data2.resourceType,
        cover: data2.coverImages[0],
        linkToDetails: FUtil.LinkTo.resourceDetails({
          resourceID: data2.resourceId,
        }),
      };
    } else {
      const params3: Parameters<typeof FServiceAPI.Storage.objectDetails>[0] = {
        objectIdOrName: actualOriginInfo.id,
      };

      const { data: data3 } = yield call(FServiceAPI.Storage.objectDetails, params3);
      relation = {
        cardTitle: '关联对象',
        identity: 'object',
        id: data3.objectId,
        name: data3.bucketName + '/' + data3.objectName,
        type: data3.resourceType,
        cover: '',
        linkToDetails: FUtil.LinkTo.objectDetails({
          bucketName: data3.bucketName,
          objectID: data3.objectId,
        }),
      };
    }
    const isChecked: boolean = testResourceDetail.resourceType.includes('主题') ? testResourceDetail.stateInfo.themeInfo.isActivatedTheme === 1 : testResourceDetail.stateInfo.onlineStatusInfo.onlineStatus === 1;
    const isDisabled: boolean = testResourceDetail.resourceType.includes('主题') && isChecked;

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: testResourceDetail.nodeId,
      isRematch: false,
    };

    const node_RuleInfo: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);

    const params1: Parameters<typeof FServiceAPI.InformalNode.batchGetAuths>[0] = {
      nodeId: testResourceDetail.nodeId,
      exhibitIds: testResourceDetail.testResourceId,
      authType: 3,
    };

    const { data: data_batchGetAuths }: { data: [{ isAuth: boolean }] } = yield call(FServiceAPI.InformalNode.batchGetAuths, params1);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        pageLoading: false,
        node_RuleInfo: node_RuleInfo,
        node_ID: testResourceDetail.nodeId,
        node_Name: data4.nodeName,
        exhibit_Identity: testResourceDetail.associatedPresentableId !== '' ? 'exhibit' : actualOriginInfo.type,
        exhibit_ResourceType: testResourceDetail.resourceType,
        exhibit_Name: testResourceDetail.testResourceName,
        exhibit_OnlineSwitchObj: {
          checked: isChecked,
          text: testResourceDetail.resourceType.includes('主题')
            ? FI18n.i18nNext.t('toggle_activate_theme')
            : FI18n.i18nNext.t('switch_set_exhibit_avaliable'),
          disabled: isDisabled,
        },
        exhibit_Info: {
          ...testResourceDetail,
          isAuth: data_batchGetAuths[0].isAuth,
        },

        side_Exhibit_Cover: testResourceDetail.stateInfo.coverInfo.coverImages[0] || '',
        side_Exhibit_Title: testResourceDetail.stateInfo.titleInfo.title || '',
        side_Exhibit_Tags: testResourceDetail.stateInfo.tagInfo.tags || [],
        side_Exhibit_AllVersions: [...actualOriginInfo.versions].reverse(),
        side_Exhibit_Version: actualOriginInfo.version,
        side_Exhibit_OnlyReadAttrs: (testResourceDetail.stateInfo.propertyInfo.testResourceProperty as any[])
          .filter((cr: any) => {
            return cr.authority === 1;
          })
          .map<InformExhibitInfoPageModelState['side_Exhibit_OnlyReadAttrs'][number]>((cr: any) => {
            return {
              theKey: cr.key,
              value: fileAttrUnits[cr.key] ? fileAttrUnits[cr.key](cr.value) : cr.value,
            };
          }),
        side_Exhibit_OnlyEditAttrs: (testResourceDetail.stateInfo.propertyInfo.testResourceProperty as any[])
          .filter((cr: any) => {
            return cr.authority === 2;
          })
          .map<InformExhibitInfoPageModelState['side_Exhibit_OnlyEditAttrs'][number]>((cr: any) => {
            return {
              theKey: cr.key,
              theValue: cr.value,
              theValueError: '',
              remark: cr.remark,
              selectOptions: cr.candidateItems,
            };
          }),
        side_Exhibit_EditDeleteAttrs: (testResourceDetail.stateInfo.propertyInfo.testResourceProperty as any[])
          .filter((cr: any) => {
            return cr.authority === 6;
          })
          .map<InformExhibitInfoPageModelState['side_Exhibit_EditDeleteAttrs'][number]>((cr: any) => {
            return {
              theKey: cr.key,
              theValue: cr.value,
              theValueError: '',
              remark: cr.remark,
            };
          }),

        side_Resource_Relation: relation,
      },
    });
  },
  * onChange_Exhibit_OnlineSwitch({ payload }: OnChange_Exhibit_OnlineSwitch_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    let rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    rules = mergeRules({
      oldRules: rules,
      exhibitName: informExhibitInfoPage.exhibit_Name,
      action: {
        operation: 'online',
        content: payload.checked,
      },
    });

    const text: string = decompile(rules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },
  * onChange_Theme_OnlineSwitch({ payload }: OnChange_Theme_OnlineSwitch_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {

    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    if (needHandleRules.some((n) => n.operation === 'activate_theme')) {
      needHandleRules = needHandleRules.map((nhr) => {
        if (nhr.operation === 'activate_theme') {
          return {
            ...nhr,
            exhibitName: informExhibitInfoPage.exhibit_Name,
            text: '',
          };
        }
        return nhr;
      });
    } else {
      needHandleRules = [
        ...needHandleRules,
        {
          operation: 'activate_theme',
          exhibitName: informExhibitInfoPage.exhibit_Name,
          text: '',
        },
      ];
    }

    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },

  * onChanged_ExhibitAuthorized({}: OnChanged_ExhibitAuthorized_Action, { put }: EffectsCommandMap) {
    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },
};
